<template>
  <!--
    今日の全体状況。誰が出ていて、誰が休みか。

    経営管理者以上だけに出す。稼働状況分析も外側の ApiAccessPolicyService が
    同じ MANAGEMENT_INSIGHT_VIEW に限っているので、範囲は同じ。
    （以前ここに「ヒートマップは事務担当者も見るので、それより狭い」と
    書いていたが、実処理を読み違えていた。2026-08-17 に訂正）
    出し分けは親が持ち、この部品は渡されたものを描くだけにする。
  -->
  <article class="preika-insight-card attendance-overview">
    <div class="preika-insight-heading">
      <div>
        <p class="preika-insight-heading__eyebrow">TODAY</p>
        <h2>今日の勤怠状況</h2>
        <p class="preika-insight-heading__description">
          勤怠管理の対象外に設定された方は表示されません。休暇申請や会社休日も反映されます。
        </p>
      </div>
      <span v-if="targetDateLabel" class="preika-insight-badge">
        {{ targetDateLabel }}
      </span>
    </div>

    <p v-if="loading" class="attendance-overview__state">読み込んでいます…</p>

    <div v-else-if="error" class="attendance-overview__state">
      <p>読み込めませんでした。</p>
      <button type="button" class="attendance-overview__retry" @click="$emit('retry')">
        もう一度
      </button>
    </div>

    <template v-else>
      <ul class="attendance-overview__counts">
        <li
          v-for="item in counts"
          :key="item.category"
          :class="['attendance-overview__count', 'is-' + item.key]"
        >
          <span class="attendance-overview__count-value">{{ item.value }}</span>
          <span class="attendance-overview__count-label">{{ item.label }}</span>
        </li>
      </ul>

      <!--
        月次締めコックピットと同じ骨組み・同じクラスを使う。
        利用者から「検索機能やUIが違う。コックピット側に合わせたい」
        （2026-08-17）。

        **中身は合わせない。**あちらはページの中だけを絞るので、
        別のページにいる人を探すと「いません」と出る。こちらは
        条件を親へ渡してサーバが全員から絞る。
      -->
      <div class="preika-close-filterbar">
        <div class="preika-close-tabs" role="tablist" aria-label="今日の勤怠状況の絞り込み">
          <button
            v-for="option in filterOptions"
            :key="option.value"
            type="button"
            :class="{ active: statusFilter === option.value }"
            @click="selectStatus(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
        <label class="preika-close-search">
          <CIcon name="cil-magnifying-glass"/>
          <input
            type="search"
            :value="keyword"
            placeholder="氏名・部署を検索"
            @change="changeKeyword($event.target.value)"
          >
        </label>
      </div>

      <p v-if="filtered" class="attendance-overview__matched">
        {{ totalCount }}名中 {{ matchedCount }}名が条件に一致
      </p>

      <p v-if="!rows.length" class="attendance-overview__state">
        {{ filtered ? '条件に一致する方がいません。' : '対象の利用者がいません。' }}
      </p>

      <!--
        1000人の会社では1ページに収まらない。**何人目まで見ているかを必ず出す。**
        出さないと「これで全部」と読まれる。
      -->
      <div v-if="rows.length && paged" class="attendance-overview__pager">
        <button
          type="button"
          class="attendance-overview__pager-button"
          :disabled="!hasPrevious"
          @click="$emit('page', Math.max(offset - pageSize, 0))"
        >前へ</button>
        <span class="attendance-overview__pager-label">
          {{ listedCount }}名中 {{ firstIndex }}〜{{ lastIndex }}名
        </span>
        <button
          type="button"
          class="attendance-overview__pager-button"
          :disabled="!hasNext"
          @click="$emit('page', offset + pageSize)"
        >次へ</button>
      </div>

      <div v-if="rows.length" class="attendance-overview__table-wrap">
        <table class="attendance-overview__table">
          <thead>
            <tr>
              <th scope="col">部署</th>
              <th scope="col">名前</th>
              <th scope="col">状態</th>
              <th scope="col">出勤</th>
              <th scope="col">退勤</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.userId">
              <td>{{ row.departmentName }}</td>
              <td>{{ row.userName }}</td>
              <td>
                <!--
                  出す名前は種別まで込みのほう（「午前有給」）。色は状態
                  （「半休」）で決めるので、午前有給と午後代休は同じ色になる。
                -->
                <span :class="['attendance-overview__status', 'is-' + statusKey(row)]">
                  {{ row.categoryLabel || row.statusLabel }}
                </span>
              </td>
              <td>{{ row.startTime || '—' }}</td>
              <td>{{ row.endTime || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </article>
</template>

<script>
import { formatDisplayDate } from '../../utils/displayDate'

/** サーバの状態名を、そのままCSSの目印に使えるようにする。 */
function statusKeyOf (status) {
  return String(status || '').toLowerCase().replace(/_/g, '')
}

export default {
  name: 'DashboardAttendanceOverview',
  props: {
    overview: { type: Object, default: () => ({}) },
    /** いま効いている検索語。親が持つ（再取得の条件なので）。 */
    keyword: { type: String, default: '' },
    /** いま効いている状態の絞り込み。 */
    statusFilter: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    error: { type: Boolean, default: false }
  },
  computed: {
    /*
     * いつの状況か（右上の札）。back は 2026-10-01 の形で返す（取り決めの値なので back は変えない）。
     * そのまま出さず、画面の日付の書き方（utils/displayDate.js で1か所に決める）に直して出す。
     * 読めない値は出さない。
     */
    targetDateLabel () {
      return formatDisplayDate(this.overview && this.overview.targetDate)
    },
    rows () {
      return Array.isArray(this.overview.rows) ? this.overview.rows : []
    },
    /*
     * 件数はサーバが数えたものをそのまま出す。
     *
     * **画面で数え直さない。** 同じ数え方を2か所に書くと、片方だけ直した
     * ときに合計と明細が食い違う。0件のものは並べない（見る人が探す手間を
     * 増やすだけ）。
     */
    counts () {
      /*
       * **タイルの並びは画面で決めない。サーバが返した順に出す。**
       *
       * 以前はここで状態を手で並べていた。back に半休と要確認を足したのに
       * ここが5枚のままで、その2つの状態の人はどのタイルにも入らず、
       * 合計が人数に合わなかった（2026-08-17 の再監査で指摘）。
       * 並べる側が一覧を持っている限り、足し忘れは必ずまた起きる。
       *
       * **サマリは粗い区分のまま。**有給・特休・午前代休…と15枚並べると、
       * 数字と文字が並んでいるだけに見えて一目で読めない。種別は明細の行
       * （categoryLabel）で出す。利用者の指示（2026-08-17）。
       *
       * value が 0 のものをサーバが返すことがある（出勤・退勤済み・
       * 出勤予定は0人でも出す。0人であること自体が情報なので）。
       */
      const fromServer = Array.isArray(this.overview.statusCounts)
        ? this.overview.statusCounts
        : null
      if (fromServer) {
        return fromServer.map(item => ({
          category: item.status,
          key: statusKeyOf(item.status),
          label: item.label,
          value: Number(item.count) || 0
        }))
      }

      /*
       * 古いサーバ（statusCounts を返さない）向け。差し替えは back が
       * 先なので通常は通らないが、順序が入れ替わったときにカードが
       * 空になるより、出るほうがよい。
       */
      return [
        { category: 'WORKING', key: 'working', label: '出勤', value: this.overview.workingCount },
        { category: 'LEFT', key: 'left', label: '退勤済み', value: this.overview.leftCount },
        { category: 'NOT_STARTED', key: 'notstarted', label: '出勤予定', value: this.overview.notStartedCount },
        { category: 'PARTIAL_LEAVE', key: 'partialleave', label: '半休', value: this.overview.partialLeaveCount },
        { category: 'ON_LEAVE', key: 'onleave', label: '休暇', value: this.overview.onLeaveCount },
        { category: 'ABSENT', key: 'absent', label: '欠勤', value: this.overview.absentCount },
        { category: 'HOLIDAY', key: 'holiday', label: '休日', value: this.overview.holidayCount },
        { category: 'UNKNOWN', key: 'unknown', label: '要確認', value: this.overview.unknownCount }
      ]
        .map(item => ({ ...item, value: Number(item.value) || 0 }))
        .filter(item => item.value > 0)
    },

    /*
     * ページ操作。**数はサーバが返したものだけを使う。**
     *
     * 画面で「たぶん何人目」と計算すると、上のタイル（全員ぶん）と
     * ずれる。1000人の会社で「これで全部」と読まれるのがいちばん困る。
     */
    totalCount () {
      return Number(this.overview.totalCount) || 0
    },
    /** 検索・絞り込みに当たった人数。サーバが全員から数えたもの。 */
    matchedCount () {
      return Number(this.overview.matchedCount) || 0
    },
    filtered () {
      return this.overview.filtered === true
    },
    /**
     * 絞り込みのボタン。
     *
     * <b>並びは画面で決めない。</b>サーバが返したタイルから作る。
     * 状態が増えたときに、ここへ足し忘れて選べなくなるのを防ぐ。
     */
    filterOptions () {
      return [{ value: '', label: '全て' }].concat(
        this.counts.map(item => ({
          value: item.category,
          label: item.label + '(' + item.value + ')'
        }))
      )
    },
    /** ページャの母数。絞り込み中は一致件数。 */
    listedCount () {
      return this.filtered ? this.matchedCount : this.totalCount
    },
    pageSize () {
      return Number(this.overview.pageSize) || 0
    },
    offset () {
      return Number(this.overview.offset) || 0
    },
    /** 1ページに収まっていないときだけ、ページ操作を出す。 */
    paged () {
      return this.pageSize > 0 && this.listedCount > this.rows.length
    },
    firstIndex () {
      return this.rows.length ? this.offset + 1 : 0
    },
    lastIndex () {
      return this.offset + this.rows.length
    },
    hasPrevious () {
      return this.offset > 0
    },
    hasNext () {
      return this.lastIndex < this.listedCount
    }
  },
  methods: {
    statusKey (row) {
      return statusKeyOf(row.status)
    },
    /*
     * 絞り込みは親へ渡す。**カードの中で絞らない。**
     * ここで絞るとページに載っている100名だけが対象になり、
     * 別のページにいる人を探せない（月次締めがその作り）。
     */
    selectStatus (category) {
      this.$emit('filter', { keyword: this.keyword, status: category })
    },
    changeKeyword (value) {
      this.$emit('filter', {
        keyword: String(value || '').trim(),
        status: this.statusFilter
      })
    },
    clearFilter () {
      this.$emit('filter', { keyword: '', status: '' })
    }
  }
}
</script>
